import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  ArrowDownToLine, ArrowUpFromLine, AlertTriangle, Pencil, Trash2, Download,
  Calendar, TrendingDown, Search, X, Eye,
  Package, DollarSign, Layers, BarChart3
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Modal, Button, FormField, Input, Select, PageHeader, Badge } from '../../components/UI';
import ConfirmModal from '../../components/ConfirmModal';
import Breadcrumb from '../../components/Breadcrumb';
import { inventoryService, medicineService } from '../../api/services';
import { formatCurrency } from '../../utils/helpers';
import { toJalali } from '../../utils/jalali';
import toast from 'react-hot-toast';

// ─── Medicine Searchable Lookup ───────────────────────────────────────────────
function MedicineLookup({ onSelect, selectedMedicine, onClear }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropPos, setDropPos] = useState({ top:0, left:0, width:0 });
  const ref = useRef(null);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const updatePos = () => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX, width: r.width });
    }
  };


  const search = useCallback(async (q) => {
    if (!q) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try { const res = await medicineService.search(q); setResults(res.data.data||[]); setOpen(true); updatePos(); }
    catch {} finally { setLoading(false); }
  }, []);

  if (selectedMedicine) return (
    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
      <div>
        <p className="text-sm font-semibold text-blue-800">{selectedMedicine.commercialName}</p>
        <p className="text-xs text-blue-500">{selectedMedicine.genericName} · {selectedMedicine.category?.name}</p>
      </div>
      <button type="button" onClick={onClear}><X size={16} className="text-blue-400"/></button>
    </div>
  );

  return (
    <div ref={ref}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10"/>
        <input
          ref={inputRef}
          value={query}
          onChange={e=>{ setQuery(e.target.value); clearTimeout(timer.current); timer.current=setTimeout(()=>search(e.target.value),300); }}
          onFocus={()=>{ if(query){ updatePos(); setOpen(true); } }}
          placeholder="Search medicine by name or barcode..."
          className="w-full pl-9 pr-3 py-3 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
        />
        {loading && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"/>}
      </div>

      {/* FIXED position portal — escapes modal overflow:hidden */}
      {open && results.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: dropPos.top,
            left: dropPos.left,
            width: dropPos.width,
            zIndex: 9999,
            maxHeight: '50vh',
            overflowY: 'auto',
            background: 'white',
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}
        >
          <div style={{padding:'8px 12px', background:'#eff6ff', borderBottom:'1px solid #bfdbfe', fontSize:'12px', color:'#1d4ed8', fontWeight:600}}>
            {results.length} result(s) found
          </div>
          {results.map(m=>(
            <button
              key={m._id} type="button"
              onClick={()=>{ onSelect(m); setQuery(''); setResults([]); setOpen(false); }}
              style={{width:'100%', textAlign:'left', padding:'10px 14px', borderBottom:'1px solid #f3f4f6', background:'white', cursor:'pointer', display:'block'}}
              onMouseEnter={e=>e.currentTarget.style.background='#eff6ff'}
              onMouseLeave={e=>e.currentTarget.style.background='white'}
            >
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:'8px'}}>
                <div style={{flex:1, minWidth:0}}>
                  <p style={{margin:0, fontWeight:600, fontSize:'14px', color:'#1f2937'}}>{m.commercialName}</p>
                  <p style={{margin:'2px 0 0', fontSize:'12px', color:'#9ca3af'}}>
                    {m.genericName}{m.strength?` · ${m.strength}`:''}{m.category?.name?` · ${m.category.name}`:''}
                  </p>
                </div>
                <span style={{fontSize:'11px', background:'#dbeafe', color:'#1d4ed8', padding:'2px 8px', borderRadius:'6px', whiteSpace:'nowrap', fontWeight:500}}>
                  {m.baseUnit}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {open && results.length===0 && query && !loading && (
        <div style={{position:'fixed', top:dropPos.top, left:dropPos.left, width:dropPos.width, zIndex:9999, background:'white', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 10px 30px rgba(0,0,0,0.1)', padding:'16px', textAlign:'center', fontSize:'14px', color:'#9ca3af'}}>
          No medicines found for "{query}"
        </div>
      )}
    </div>
  );
}

// ─── Stats Row ────────────────────────────────────────────────────────────────
function StatsRow({ refresh }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    inventoryService.getOverview().then(r=>setStats(r.data.data)).catch(()=>{});
  }, [refresh]); // eslint-disable-line

  if (!stats) return <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">{Array(7).fill(0).map((_,i)=><div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse"/>)}</div>;

  const cards = [
    { label:'Total Medicines',  value:stats.totalMedicines,  icon:Package,   color:'blue'   },
    { label:'Total Stock',      value:stats.totalStock,      icon:Layers,    color:'gray'   },
    { label:'Inventory Value',  value:`AFN ${(stats.totalValue||0).toLocaleString()}`, icon:DollarSign, color:'green' },
    { label:'Low Stock',        value:stats.lowStock,        icon:TrendingDown, color:'orange' },
    { label:'Out of Stock',     value:stats.outOfStock,      icon:AlertTriangle, color:'red'  },
    { label:'Expired',          value:stats.expired,         icon:Calendar,  color:'red'    },
    { label:'Near Expiry',      value:stats.nearExpiry,      icon:Calendar,  color:'yellow' },
  ];

  const colorMap = {
    blue:  'bg-blue-50 border-blue-200 text-blue-700',
    gray:  'bg-gray-50 border-gray-200 text-gray-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    orange:'bg-orange-50 border-orange-200 text-orange-700',
    red:   'bg-red-50 border-red-200 text-red-700',
    yellow:'bg-yellow-50 border-yellow-200 text-yellow-700',
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
      {cards.map(card=>(
        <div key={card.label} className={`rounded-xl border p-3 ${colorMap[card.color]}`}>
          <div className="flex items-center gap-1.5 mb-1">
            <card.icon size={13}/>
            <p className="text-xs font-medium leading-tight">{card.label}</p>
          </div>
          <p className="text-xl font-bold">{card.value}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────
function DetailModal({ item, onClose }) {
  if (!item) return null;
  const med = item.medicine || {};
  const now = new Date();
  const expiryDiff = item.expiryDate ? Math.ceil((new Date(item.expiryDate)-now)/(1000*60*60*24)) : null;
  const expiryStatus = expiryDiff === null ? null : expiryDiff < 0 ? 'expired' : expiryDiff < 30 ? 'critical' : expiryDiff < 90 ? 'warning' : 'ok';

  const InfoRow = ({label, value, highlight}) => (
    <div className={`flex justify-between py-2 border-b border-gray-50 ${highlight?'bg-blue-50 -mx-4 px-4':''}`}>
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm font-medium ${highlight?'text-blue-700':' text-gray-800'}`}>{value}</span>
    </div>
  );

  return (
    <Modal isOpen={!!item} onClose={onClose} title="Inventory Details" size="lg">
      <div className="space-y-5">
        {/* Medicine info */}
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base font-bold text-gray-900">{med.commercialName}</p>
              <p className="text-sm text-gray-500">{med.genericName} {med.strength?`· ${med.strength}`:''}</p>
              <div className="flex gap-2 mt-1.5">
                {med.drugForm && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full capitalize">{med.drugForm}</span>}
                {med.category?.name && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{med.category.name}</span>}
              </div>
            </div>
            <Badge color={med.isActive?'green':'gray'}>{med.isActive?'Active':'Inactive'}</Badge>
          </div>
        </div>

        {/* Stock numbers */}
        <div className="grid grid-cols-4 gap-3 text-center">
          {[
            { label:'Stock In',    value:item.totalIn||0,      color:'text-green-700', bg:'bg-green-50' },
            { label:'Stock Out',   value:item.totalOut||0,     color:'text-red-600',   bg:'bg-red-50'   },
            { label:'Current',     value:item.currentStock||0, color:'text-blue-700',  bg:'bg-blue-50'  },
            { label:'Available',   value:item.availableQty||0, color:'text-gray-800',  bg:'bg-gray-50'  },
          ].map(s=>(
            <div key={s.label} className={`${s.bg} rounded-xl p-3`}>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Details */}
        <div>
          <InfoRow label="Base Unit"     value={med.baseUnit || '-'} highlight />
          <InfoRow label="Purchase Price" value={formatCurrency(item.purchasePrice||0)} />
          <InfoRow label="Selling Price"  value={formatCurrency(item.sellingPrice||0)} />
          <InfoRow label="Inventory Value" value={formatCurrency((item.currentStock||0)*(item.purchasePrice||0))} highlight/>
          {item.batchNumber && <InfoRow label="Batch Number" value={item.batchNumber}/>}
          {item.invoiceNumber && <InfoRow label="Invoice #" value={item.invoiceNumber}/>}
          {item.expiryDate && (
            <InfoRow
              label="Expiry Date"
              value={
                <span className={
                  expiryStatus==='expired' ? 'text-red-600 font-bold' :
                  expiryStatus==='critical'? 'text-red-500' :
                  expiryStatus==='warning' ? 'text-orange-500' : 'text-gray-800'
                }>
                  {toJalali(item.expiryDate)}
                  {expiryDiff !== null && (
                    <span className="ml-1 text-xs">
                      {expiryDiff < 0 ? `(${Math.abs(expiryDiff)}d ago)` : `(${expiryDiff}d left)`}
                    </span>
                  )}
                </span>
              }
            />
          )}
          {item.branch?.name && <InfoRow label="Branch" value={item.branch.name}/>}
        </div>

        {/* Sales units + Stock breakdown */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Stock by Unit</p>
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400">Unit</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-400">= {med.baseUnit||'base'}</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-400">Sell Price</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-blue-600">Available</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-400">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {/* Base unit row */}
                <tr className="bg-blue-50">
                  <td className="px-3 py-2.5 font-semibold text-blue-700">
                    {med.baseUnit||'Base'} <span className="text-xs text-blue-400">(base)</span>
                  </td>
                  <td className="px-3 py-2.5 text-center text-blue-600">1</td>
                  <td className="px-3 py-2.5 text-right">{formatCurrency(item.sellingPrice||0)}</td>
                  <td className="px-3 py-2.5 text-right font-bold text-blue-700">{item.availableQty||0}</td>
                  <td className="px-3 py-2.5 text-right text-gray-500">{formatCurrency((item.availableQty||0)*(item.sellingPrice||0))}</td>
                </tr>
                {/* Sales unit rows */}
                {(med.salesUnits||[]).map(u=>{
                  const avail = Math.floor((item.availableQty||0)/u.conversionToBase);
                  const remainder = (item.availableQty||0) % u.conversionToBase;
                  return (
                    <tr key={u.unitKey} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 font-medium text-gray-800">{u.unitName}</td>
                      <td className="px-3 py-2.5 text-center text-gray-500">{u.conversionToBase}</td>
                      <td className="px-3 py-2.5 text-right">{formatCurrency(u.sellingPrice||0)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-gray-700">
                        {avail}
                        {remainder>0 && <span className="text-xs text-gray-400 ml-1">+ {remainder} {med.baseUnit}</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right text-gray-500">{formatCurrency(avail*(u.sellingPrice||0))}</td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Summary row */}
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colSpan={3} className="px-3 py-2.5 text-xs font-semibold text-gray-600">
                    Total Base Units: <strong>{item.availableQty||0} {med.baseUnit}</strong>
                    {(med.salesUnits||[]).length>0 && (() => {
                      const parts = (med.salesUnits||[]).map(u=>({
                        name:u.unitName, qty:Math.floor((item.availableQty||0)/u.conversionToBase),
                        rem:(item.availableQty||0)%u.conversionToBase
                      }));
                      const biggest = parts[parts.length-1];
                      return biggest?.qty>0 ? ` · ${biggest.qty} ${biggest.name}${biggest.rem>0?` + ${biggest.rem} ${med.baseUnit}`:''}` : '';
                    })()}
                  </td>
                  <td className="px-3 py-2.5 text-right font-bold text-blue-700">{item.availableQty||0}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-green-700">{formatCurrency((item.availableQty||0)*(item.sellingPrice||0))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function InventoryPage() {
  const [data, setData]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(false);
  const [page, setPage]           = useState(1);
  const [filter, setFilter]       = useState('all');
  const [search, setSearch]       = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimer = useRef(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [statsKey, setStatsKey]   = useState(0); // to refresh stats
  const [txData, setTxData]       = useState([]);
  const [txTotal, setTxTotal]     = useState(0);
  const [txPage, setTxPage]       = useState(1);
  const [txLoading, setTxLoading] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [stockInOpen,  setStockInOpen]  = useState(false);
  const [salesUnitsForm, setSalesUnitsForm] = useState([]);
  const updateSU = (idx, field, val) => setSalesUnitsForm(prev => prev.map((s,i)=>i===idx?{...s,[field]:val}:s));
  const [stockOutOpen, setStockOutOpen] = useState(false);
  const [detailItem, setDetailItem]     = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);
  const [editTarget,   setEditTarget]   = useState(null);
  const [editForm,     setEditForm]     = useState({});
  const [editSaving,   setEditSaving]   = useState(false);
  const [selectedMedIn,  setSelectedMedIn]  = useState(null);
  const [selectedMedOut, setSelectedMedOut] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);

  const inForm  = useForm();
  const outForm = useForm();


  

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const params = { page, limit:20, search };
      if (filter==='lowStock')    params.lowStock   = true;
      if (filter==='nearExpiry')  params.nearExpiry = true;
      if (filter==='expired')     params.expired    = true;
      if (filter==='outOfStock')  params.outOfStock = true;
      const res = await inventoryService.getAll(params);
      setData(res.data.data||[]);
      setTotal(res.data.pagination?.total||0);
    } catch { toast.error('Failed to load inventory'); }
    finally { setLoading(false); }
  };

  const [pendingSyncCount, setPendingSyncCount] = useState(0);

  const fetchTx = async () => {
    setTxLoading(true);
    try {
      const res = await inventoryService.getTransactions({ page:txPage, limit:20 });
      setTxData(res.data.data||[]);
      setTxTotal(res.data.pagination?.total||0);
    } catch {} finally { setTxLoading(false); }
  };

  // اگر تغییراتی (مثلاً یک فروش) هنوز آفلاین ثبت شده و سنس نشده باشد، اینجا
  // (چون سنس دستی است) هنوز در سرور دیده نمی‌شود — یک پیام توضیح می‌دهیم
  // تا معلوم شود دلیلش نبود باگ بلکه در انتظار سنس بودن است.
  const refreshPendingSyncCount = async () => {
    try { setPendingSyncCount(await (window.pharmaSync?.getQueueCount?.() ?? 0)); } catch { setPendingSyncCount(0); }
  };

  useEffect(() => { fetchSummary(); }, [page, filter, search]); // eslint-disable-line

  // When a sync finishes (login, reconnect, the 1-minute retry, the Sync button)
  // the server's numbers have just replaced the local ones — reload the list and
  // the cards so the screen shows them instead of the pre-sync figures.
  const reloadRef = useRef(null);
  reloadRef.current = () => { fetchSummary(); setStatsKey(k => k + 1); };
  useEffect(() => {
    const off = window.pharmaSync?.onSyncEvent?.(evt => { if (evt?.type === 'sync-end') reloadRef.current?.(); });
    return () => { if (typeof off === 'function') off(); };
  }, []);
  useEffect(() => { if (activeTab==='transactions') { fetchTx(); refreshPendingSyncCount(); } }, [activeTab, txPage]); // eslint-disable-line

  // When medicine selected for Stock In — auto-fill fields
  const handleExportOutOfStock = async () => {
    try {
      const r = await inventoryService.exportOutOfStock();
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `out-of-stock-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click(); URL.revokeObjectURL(url); toast.success('Excel downloaded');
    } catch { toast.error('Export failed'); }
  };

  const handleExportNearExpiryExcel = async () => {
    try {
      const r = await inventoryService.exportNearExpiryExcel();
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `near-expiry-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click(); URL.revokeObjectURL(url); toast.success('Excel downloaded');
    } catch { toast.error('Export failed'); }
  };

  const handleExportNearExpiryPdf = async () => {
    try {
      const r = await inventoryService.exportNearExpiryPdf();
      const w = window.open('', '_blank');
      w.document.write(r.data); w.document.close();
    } catch { toast.error('PDF failed'); }
  };

  const handleInventoryDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await inventoryService.deleteRecord(deleteTarget._id);
      toast.success('Inventory record deleted');
      setDeleteTarget(null); fetchSummary(); setStatsKey(k=>k+1);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setDeleting(false); }
  };

  const openEdit = (row) => {
    setEditTarget(row);
    setEditForm({
      currentStock:  row.currentStock ?? '',
      purchasePrice: row.purchasePrice ?? '',
      sellingPrice:  row.sellingPrice ?? '',
      batchNumber:   row.batchNumber ?? '',
      expiryDate:    row.expiryDate ? (() => {
        const d = new Date(row.expiryDate);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      })() : '',
      notes:         row.notes ?? '',
    });
  };

  const handleEditSave = async () => {
  if (!editTarget) return;
  setEditSaving(true);
  try {
    // فقط فیلدهایی که در مدل InventorySummary وجود دارد
    const payload = {
      currentStock: editForm.currentStock !== '' ? Number(editForm.currentStock) : undefined,
      purchasePrice: editForm.purchasePrice ? Number(editForm.purchasePrice) : 0,
      sellingPrice:  editForm.sellingPrice ? Number(editForm.sellingPrice) : 0,
      batchNumber:   editForm.batchNumber || '',
      expiryDate:    editForm.expiryDate || null,
      notes:         editForm.notes || '',
    };
    await inventoryService.updateRecord(editTarget._id, payload);
    toast.success('Inventory record updated');
    setEditTarget(null);
    fetchSummary();
    setStatsKey(k => k + 1);
  } catch (e) {
    toast.error(e.response?.data?.message || 'Failed');
  } finally {
    setEditSaving(false);
  }
};

  const onSelectMedOut = (med) => {
    setSelectedMedOut(med);
    outForm.setValue('unit', med.baseUnit || 'strip');
  };

  const onStockIn = async (data) => {
    if (!selectedMedIn) return toast.error('Select a medicine');
    
    // حالت ویرایش - آپدیت رکورد موجود
    if (editingRecord) {
      setSaving(true);
      try {
        const payload = {};
        if (data.purchasePrice !== undefined && data.purchasePrice !== '') payload.purchasePrice = Number(data.purchasePrice);
        if (data.sellingPrice !== undefined && data.sellingPrice !== '') payload.sellingPrice = Number(data.sellingPrice);
        if (data.batchNumber !== undefined) payload.batchNumber = data.batchNumber;
        if (data.expiryDate) payload.expiryDate = data.expiryDate;
        if (data.notes !== undefined) payload.notes = data.notes;
        
        await inventoryService.updateRecord(editingRecord._id, payload);
        
        // آپدیت کارت دارو
        const cleanSalesUnits = salesUnitsForm
          .filter(u => u.unitName && u.unitKey && u.conversionToBase > 0)
          .map(u => ({
            unitName: u.unitName.trim(),
            unitKey: u.unitKey.trim().toLowerCase().replace(/\s+/g, '_'),
            conversionToBase: Number(u.conversionToBase),
            sellingPrice: Number(u.sellingPrice) || 0,
          }));
  
        await medicineService.update(selectedMedIn._id, {
          baseUnit: data.baseUnit,
          purchasePrice: Number(data.purchasePrice),
          sellingPrice: Number(data.sellingPrice),
          salesUnits: cleanSalesUnits,
        });
  
        toast.success('Inventory record updated');
        setStockInOpen(false);
        setSelectedMedIn(null);
        setEditingRecord(null);
        inForm.reset();
        setSalesUnitsForm([]);
        fetchSummary();
        setStatsKey(k => k + 1);
      } catch (e) {
        toast.error(e.response?.data?.message || 'Failed to update');
      } finally {
        setSaving(false);
      }
      return;
    }
    
    // حالت ورودی جدید
    if (!data.purchasePrice || !data.sellingPrice) {
      return toast.error('Purchase price and selling price are required');
    }
    if (!data.quantity || Number(data.quantity) <= 0) {
      return toast.error('Quantity must be greater than 0');
    }
    
    setSaving(true);
    try {
      await inventoryService.stockIn({
        medicineId: selectedMedIn._id,
        quantity: Number(data.quantity),
        unit: data.unit || data.baseUnit,
        batchNumber: data.batchNumber,
        expiryDate: data.expiryDate,
        productionDate: data.productionDate,
        purchasePrice: Number(data.purchasePrice),
        sellingPrice: Number(data.sellingPrice),
        notes: data.notes,
      });
  
      const cleanSalesUnits = salesUnitsForm
        .filter(u => u.unitName && u.unitKey && u.conversionToBase > 0)
        .map(u => ({
          unitName: u.unitName.trim(),
          unitKey: u.unitKey.trim().toLowerCase().replace(/\s+/g, '_'),
          conversionToBase: Number(u.conversionToBase),
          sellingPrice: Number(u.sellingPrice) || 0,
        }));
  
      await medicineService.update(selectedMedIn._id, {
        baseUnit: data.baseUnit,
        purchasePrice: Number(data.purchasePrice),
        sellingPrice: Number(data.sellingPrice),
        salesUnits: cleanSalesUnits,
      });
  
      toast.success('Stock In recorded. Medicine card updated.');
      setStockInOpen(false);
      setSelectedMedIn(null);
      setEditingRecord(null);
      inForm.reset();
      setSalesUnitsForm([]);
      fetchSummary();
      setStatsKey(k => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const onStockOut = async (data) => {
    if (!selectedMedOut) return toast.error('Select a medicine');
    setSaving(true);
    try {
      await inventoryService.stockOut({
        medicineId:  selectedMedOut._id,
        quantity:    Number(data.quantity),
        unit:        data.unit,
        batchNumber: data.batchNumber,
        notes:       data.notes || data.reason,
        reason:      data.reason,
      });
      toast.success('Stock Out recorded');
      setStockOutOpen(false); setSelectedMedOut(null); outForm.reset();
      fetchSummary(); setStatsKey(k=>k+1);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const getExpiryStatus = (date) => {
    if (!date) return null;
    const diff = (new Date(date)-new Date())/(1000*60*60*24);
    if (diff < 0)   return { label:'Expired',      color:'red'    };
    if (diff < 30)  return { label:'< 30 days',    color:'red'    };
    if (diff < 90)  return { label:'Near Expiry',  color:'orange' };
    return null;
  };

  const FILTERS = [
    { key:'all',        label:'All Stock' },
    { key:'lowStock',   label:'Low Stock',   icon:TrendingDown  },
    { key:'outOfStock', label:'Out of Stock',icon:AlertTriangle },
    { key:'nearExpiry', label:'Near Expiry', icon:Calendar      },
    { key:'expired',    label:'Expired',     icon:AlertTriangle },
  ];


  const onSelectMedIn = (med, existingRecord = null) => {
    setSelectedMedIn(med);
    setEditingRecord(existingRecord); // ذخیره رکورد برای آپدیت
    
    inForm.setValue('baseUnit', med.baseUnit || 'strip');
    inForm.setValue('unit', med.baseUnit || 'strip');
    inForm.setValue('purchasePrice', existingRecord?.purchasePrice ?? med.purchasePrice ?? '');
    inForm.setValue('sellingPrice', existingRecord?.sellingPrice ?? med.sellingPrice ?? '');
    inForm.setValue('batchNumber', existingRecord?.batchNumber ?? '');
    inForm.setValue('expiryDate', existingRecord?.expiryDate ? new Date(existingRecord.expiryDate).toISOString().split('T')[0] : '');
    inForm.setValue('quantity', existingRecord?.currentStock || '');
    inForm.setValue('notes', existingRecord?.notes ?? '');
    
    setSalesUnitsForm((med.salesUnits || []).map(u => ({
      unitName: u.unitName, unitKey: u.unitKey,
      conversionToBase: u.conversionToBase, sellingPrice: u.sellingPrice || ''
    })));
  };
    

  return (
    <div>
      <Breadcrumb/>
      <PageHeader title="Inventory" subtitle="Stock levels, transactions and warehouse management"
        actions={
          <div className="flex gap-2">
            <Button onClick={()=>{ inForm.reset(); setSelectedMedIn(null); setStockInOpen(true); }}
              className="bg-green-600 hover:bg-green-700 text-white">
              <ArrowDownToLine size={16}/> Stock In
            </Button>
            <Button variant="outline" onClick={()=>{ outForm.reset(); setSelectedMedOut(null); setStockOutOpen(true); }}>
              <ArrowUpFromLine size={16}/> Stock Out
            </Button>
          </div>
        }
      />

      {/* Stats */}
      <StatsRow refresh={statsKey}/>

      {/* Export Buttons for Near Expiry */}
      {filter==='nearExpiry' && (
        <div className="flex gap-2 mb-2">
          <button onClick={handleExportNearExpiryExcel}
            className="flex items-center gap-1.5 text-xs px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">
            <Download size={13}/> Export Excel
          </button>
          <button onClick={handleExportNearExpiryPdf}
            className="flex items-center gap-1.5 text-xs px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">
            <Download size={13}/> Export PDF (Print)
          </button>
        </div>
      )}
      {filter==='outOfStock' && (
        <div className="flex gap-2 mb-2">
          <button onClick={handleExportOutOfStock}
            className="flex items-center gap-1.5 text-xs px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">
            <Download size={13}/> Export Excel (Out of Stock)
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {[{k:'summary',l:'Stock Summary'},{k:'transactions',l:'Transactions'}].map(t=>(
          <button key={t.k} onClick={()=>setActiveTab(t.k)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab===t.k?'bg-blue-600 text-white':'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {t.l}
          </button>
        ))}
      </div>

      {/* ── Summary Tab ── */}
      {activeTab==='summary' && (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            {FILTERS.map(({key,label,icon:Icon})=>(
              <button key={key} onClick={()=>setFilter(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter===key?'bg-blue-600 text-white':'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                {Icon && <Icon size={13}/>}{label}
              </button>
            ))}
            <div className="relative ml-auto">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input value={searchInput} onChange={e=>{
                  // Debounced: typing itself stays instant, but the actual
                  // inventory fetch (which re-reads/filters the whole local
                  // cache offline, or hits the network online) only fires
                  // once you pause for a moment — not on every keystroke.
                  const val = e.target.value;
                  setSearchInput(val);
                  clearTimeout(searchTimer.current);
                  searchTimer.current = setTimeout(() => setSearch(val), 350);
                }} placeholder="Search..."
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-44"/>
            </div>
          </div>

          {/* Desktop */}
          <div className="hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Medicine</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Base Unit</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Buy Price</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Sell Price</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-green-600">In</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-red-500">Out</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-blue-600">Current</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Expiry</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? Array(5).fill(0).map((_,i)=>(
                  <tr key={i}>{Array(10).fill(0).map((_,j)=><td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td>)}</tr>
                )) : data.length===0 ? (
                  <tr><td colSpan={10} className="text-center py-12 text-gray-400">No inventory records</td></tr>
                ) : data.map(row=>{
                  const ex   = getExpiryStatus(row.expiryDate);
                  const isLow = row.currentStock <= 10 && row.currentStock > 0;
                  const isOut = row.currentStock <= 0;
                  return (
                    <tr key={row._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800">{row.medicine?.commercialName}</p>
                        <p className="text-xs text-gray-400">
                          {row.medicine?.genericName}
                          {row.medicine?.strength  ? ` · ${row.medicine.strength}`  : ''}
                          {row.medicine?.drugForm   ? ` · ${row.medicine.drugForm}`   : ''}
                          {row.batchNumber ? ` · Batch: ${row.batchNumber}` : ''}
                          {row.invoiceNumber ? ` · Inv: ${row.invoiceNumber}` : ''}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-xs capitalize">
                        <span className="bg-gray-100 px-2 py-0.5 rounded">{row.medicine?.baseUnit||'-'}</span>
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-gray-600">{formatCurrency(row.purchasePrice||0)}</td>
                      <td className="px-4 py-3 text-right text-xs font-medium text-gray-800">{formatCurrency(row.sellingPrice||0)}</td>
                      <td className="px-4 py-3 text-center font-medium text-green-700">{row.totalIn||0}</td>
                      <td className="px-4 py-3 text-center font-medium text-red-500">{row.totalOut||0}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-bold text-base ${isOut?'text-red-600':isLow?'text-orange-600':'text-gray-800'}`}>{row.currentStock||0}</span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {row.expiryDate ? <span className={ex?ex.color==='red'?'text-red-600 font-medium':'text-orange-500':'text-gray-600'}>{toJalali(row.expiryDate)}</span> : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {isOut && <Badge color="red">Out of Stock</Badge>}
                          {isLow && !isOut && <Badge color="orange">Low</Badge>}
                          {ex && <Badge color={ex.color}>{ex.label}</Badge>}
                          {!isOut && !isLow && !ex && <Badge color="green">OK</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={()=>setDetailItem(row)}
                            className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="View Details">
                            <Eye size={14}/>
                          </button>
                          <button onClick={()=>openEdit(row)}
                            className="p-1.5 rounded hover:bg-green-50 text-gray-400 hover:text-green-600" title="Edit Record">
                            <Pencil size={14}/>
                          </button>
                          <button onClick={()=>{ setStockInOpen(true); onSelectMedIn(row.medicine, row); }}
                            className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="Stock In">
                            <ArrowDownToLine size={14}/>
                          </button>
                          <button onClick={()=>setDeleteTarget(row)}
                            className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Delete Record">
                            <Trash2 size={14}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {total>20&&(
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <p className="text-xs text-gray-500">{((page-1)*20)+1}–{Math.min(page*20,total)} of {total}</p>
                <div className="flex gap-1">
                  <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Prev</button>
                  <span className="px-3 py-1.5 text-xs text-gray-600">Page {page}</span>
                  <button onClick={()=>setPage(p=>p+1)} disabled={page*20>=total} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {loading ? Array(4).fill(0).map((_,i)=><div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse"/>) :
              data.map(row=>{
                const ex   = getExpiryStatus(row.expiryDate);
                const isLow = row.currentStock<=10&&row.currentStock>0;
                const isOut = row.currentStock<=0;
                return (
                  <div key={row._id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-gray-900">{row.medicine?.commercialName}</p>
                        <p className="text-xs text-gray-400">{row.medicine?.genericName} · {row.medicine?.baseUnit}</p>
                      </div>
                      <button onClick={()=>setDetailItem(row)} className="p-1.5 rounded bg-blue-50 text-blue-600"><Eye size={14}/></button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs text-center mb-2">
                      <div className="bg-green-50 rounded p-2"><p className="text-green-700 font-bold text-base">{row.totalIn||0}</p><p className="text-gray-400">In</p></div>
                      <div className="bg-red-50 rounded p-2"><p className="text-red-500 font-bold text-base">{row.totalOut||0}</p><p className="text-gray-400">Out</p></div>
                      <div className={`rounded p-2 ${isOut?'bg-red-50':isLow?'bg-orange-50':'bg-blue-50'}`}>
                        <p className={`font-bold text-base ${isOut?'text-red-600':isLow?'text-orange-600':'text-blue-700'}`}>{row.currentStock||0}</p>
                        <p className="text-gray-400">Current</p>
                      </div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Buy: {formatCurrency(row.purchasePrice||0)} · Sell: {formatCurrency(row.sellingPrice||0)}</span>
                      {ex && <Badge color={ex.color}>{ex.label}</Badge>}
                    </div>
                  </div>
                );
              })
            }
          </div>
        </>
      )}

      {/* ── Transactions Tab ── */}
      {activeTab==='transactions' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {pendingSyncCount > 0 && (
            <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100 text-xs text-amber-800 flex items-center justify-between gap-2">
              <span>⚠ {pendingSyncCount} تغییر آفلاین (مثلاً فروش) هنوز با سرور سنس نشده، برای همین اینجا دیده نمی‌شود. از دکمه «سنس کردن» بالای صفحه استفاده کنید، بعد اینجا را دوباره بررسی کنید.</span>
              <button onClick={() => { fetchTx(); refreshPendingSyncCount(); }} className="shrink-0 text-amber-900 font-semibold underline">تازه‌سازی</button>
            </div>
          )}
          <table className="w-full text-sm hidden md:table">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Medicine</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Qty</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Unit</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Base Qty</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Batch</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Invoice #</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">By</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {txLoading ? Array(5).fill(0).map((_,i)=><tr key={i}><td colSpan={9} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td></tr>) :
                txData.map(tx=>(
                  <tr key={tx._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`flex items-center gap-1.5 text-xs font-semibold ${(tx.type==='stock_in'||(tx.type==='adjustment'&&tx.quantity>0))?'text-green-700':'text-red-600'}`}>
                        {(tx.type==='stock_in'||(tx.type==='adjustment'&&tx.quantity>0))?<ArrowDownToLine size={13}/>:<ArrowUpFromLine size={13}/>}
                        {tx.type==='adjustment' ? `ADJUSTMENT (${tx.quantity>0?'+':''}${tx.quantity})` : tx.type.replace('_',' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{tx.medicine?.commercialName}</p>
                      <p className="text-xs text-gray-400">{tx.medicine?.genericName}</p>
                    </td>
                    <td className="px-4 py-3 text-center font-medium">{tx.quantity}</td>
                    <td className="px-4 py-3 text-gray-500 capitalize">{tx.unit}</td>
                    <td className="px-4 py-3 text-center text-gray-400 text-xs">{tx.baseQuantity}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{tx.batchNumber||'-'}</td>
                    <td className="px-4 py-3 text-xs">
                      {tx.invoiceNumber
                        ? <span className="font-mono text-green-700 bg-green-50 px-2 py-0.5 rounded">{tx.invoiceNumber}</span>
                        : tx.type==='stock_out' && tx.reference
                          ? <span className="font-mono text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{tx.reference}</span>
                          : tx.type==='stock_in' && tx.notes
                            ? <span className="font-mono text-green-700 bg-green-50 px-2 py-0.5 rounded">{tx.notes.replace('From PO: ','').replace('Supplier Inv: ','')}</span>
                            : <span className="text-gray-300">-</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{tx.performedBy?.name||'-'}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{toJalali(tx.createdAt)}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
          <div className="md:hidden divide-y divide-gray-50">
            {txData.map(tx=>(
              <div key={tx._id} className="p-4">
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-xs font-bold flex items-center gap-1 ${(tx.type==='stock_in'||(tx.type==='adjustment'&&tx.quantity>0))?'text-green-700':'text-red-600'}`}>
                    {(tx.type==='stock_in'||(tx.type==='adjustment'&&tx.quantity>0))?<ArrowDownToLine size={12}/>:<ArrowUpFromLine size={12}/>}
                    {tx.type==='adjustment' ? `ADJUSTMENT (${tx.quantity>0?'+':''}${tx.quantity})` : tx.type.replace('_',' ').toUpperCase()}
                  </span>
                  <span className="text-xs text-gray-400">{toJalali(tx.createdAt)}</span>
                </div>
                {tx.type==='stock_out' && tx.reference && (
                  <p className="text-xs text-blue-700 mb-1">Invoice: <span className="font-mono">{tx.reference}</span></p>
                )}
                {tx.type==='stock_in' && tx.notes && (
                  <p className="text-xs text-green-700 mb-1">PO: <span className="font-mono">{tx.notes.replace('From PO: ','')}</span></p>
                )}
                <p className="text-sm font-medium text-gray-800">{tx.medicine?.commercialName}</p>
                <p className="text-xs text-gray-400">{tx.quantity} {tx.unit} ({tx.baseQuantity} base)</p>
              </div>
            ))}
          </div>
          {txTotal>20&&(
            <div className="flex justify-between items-center px-4 py-3 border-t border-gray-100">
              <p className="text-xs text-gray-500">Total: {txTotal}</p>
              <div className="flex gap-1">
                <button onClick={()=>setTxPage(p=>Math.max(1,p-1))} disabled={txPage===1} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Prev</button>
                <span className="px-3 py-1.5 text-xs text-gray-600">Page {txPage}</span>
                <button onClick={()=>setTxPage(p=>p+1)} disabled={txPage*20>=txTotal} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Stock In Modal ── */}
      <Modal isOpen={stockInOpen} onClose={()=>setStockInOpen(false)} title="Stock In" size="xl">
        <form onSubmit={inForm.handleSubmit(onStockIn)} className="space-y-5">
          <FormField label="Select Medicine" required>
            <MedicineLookup onSelect={onSelectMedIn} selectedMedicine={selectedMedIn}
              onClear={()=>{ setSelectedMedIn(null); inForm.reset(); setSalesUnitsForm([]); }}/>
          </FormField>

          {selectedMedIn && (
            <>
              {/* ── Base Unit & Pricing ── */}
              <div className="border border-blue-200 rounded-xl p-4 bg-blue-50/30">
                <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-3">Base Unit & Pricing</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField label="Base Unit" required>
                    <Select {...inForm.register('baseUnit',{required:'Required'})} defaultValue={selectedMedIn.baseUnit||'strip'}>
                      {[
                        'Piece','Strip','Pack','Box','Carton','Bottle','Tube',
                        'Tablet','Capsule','Ampule','Vial','Sachet',
                        'Injection','Infusion','Serum','Syrup','Cream','Ointment','Drops'
                      ].map(u=>(
                        <option key={u} value={u.toLowerCase()}>{u}</option>
                      ))}
                    </Select>
                  </FormField>
                  <FormField label="Purchase Price / base unit" required>
                    <Input type="number" step="0.01" min="0"
                      {...inForm.register('purchasePrice',{required:'Required'})} placeholder="0"/>
                  </FormField>
                  <FormField label="Selling Price / base unit" required>
                    <Input type="number" step="0.01" min="0"
                      {...inForm.register('sellingPrice',{required:'Required'})} placeholder="0"/>
                  </FormField>
                </div>
              </div>

              {/* ── Opening / Receiving Quantity ── */}
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Quantity" required>
                  <Input type="number" min="1"
                    {...inForm.register('quantity',{required:'Required'})} placeholder="0"/>
                </FormField>
                <FormField label="Receiving Unit">
                  <Select {...inForm.register('unit')}>
                    <option value={selectedMedIn.baseUnit}>{selectedMedIn.baseUnit} (base unit)</option>
                    {salesUnitsForm.filter(u=>u.unitName&&u.conversionToBase>0).map((u,i)=>(
                      <option key={i} value={u.unitKey||u.unitName?.toLowerCase()}>
                        {u.unitName} (= {u.conversionToBase} {selectedMedIn.baseUnit})
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField label="Batch Number">
                  <Input {...inForm.register('batchNumber')} placeholder="Batch No."/>
                </FormField>
                <FormField label="Expiry Date">
                  <Input type="date" {...inForm.register('expiryDate')}/>
                </FormField>
                <FormField label="Production Date">
                  <Input type="date" {...inForm.register('productionDate')}/>
                </FormField>
                <FormField label="Notes">
                  <Input {...inForm.register('notes')} placeholder="Optional"/>
                </FormField>
              </div>

              {/* ── Sales Units ── */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Sales Units</p>
                    <p className="text-xs text-gray-400 mt-0.5">Define units for selling — system auto-converts to base unit</p>
                  </div>
                  <button type="button"
                    onClick={()=>setSalesUnitsForm(prev=>[...prev,{unitName:'',unitKey:'',conversionToBase:1,sellingPrice:''}])}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                    + Add Unit
                  </button>
                </div>

                {salesUnitsForm.length===0 ? (
                  <div className="px-4 py-4 text-xs text-gray-400 text-center">
                    No sales units defined. Example: 1 Box = 3 Strips
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Unit Name</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Key</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">
                          = how many {inForm.watch('baseUnit')||selectedMedIn.baseUnit}(s)
                        </th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Sell Price / unit</th>
                        <th className="w-8"/>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {salesUnitsForm.map((su,idx)=>(
                        <tr key={idx}>
                          <td className="px-3 py-2">
                            <select value={su.unitName} onChange={e=>{ updateSU(idx,'unitName',e.target.value); updateSU(idx,'unitKey',e.target.value.toLowerCase()); }}
                            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                            <option value="">Select unit</option>
                            {['Piece','Strip','Pack','Box','Carton','Bottle','Tube','Tablet','Capsule','Ampule','Vial','Sachet','Injection','Infusion','Serum','Syrup','Cream','Ointment','Drops'].map(u=>(
                              <option key={u} value={u}>{u}</option>
                            ))}
                          </select>
                          </td>
                          <td className="px-3 py-2">
                            <input value={su.unitKey} onChange={e=>updateSU(idx,'unitKey',e.target.value)}
                              placeholder="e.g. box"
                              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                          </td>
                          <td className="px-3 py-2">
                            <input type="number" min="1" value={su.conversionToBase}
                              onChange={e=>updateSU(idx,'conversionToBase',Number(e.target.value))}
                              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                            {su.conversionToBase>1&&su.unitName&&(
                              <p className="text-xs text-blue-500 mt-0.5">
                                1 {su.unitName} = {su.conversionToBase} {inForm.watch('baseUnit')||selectedMedIn.baseUnit}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <input type="number" step="0.01" min="0" value={su.sellingPrice}
                              onChange={e=>updateSU(idx,'sellingPrice',e.target.value)}
                              placeholder="0"
                              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                            {Number(su.sellingPrice)>0&&su.conversionToBase>1&&(
                              <p className="text-xs text-gray-400 mt-0.5">
                                ≈ {formatCurrency(Number(su.sellingPrice)/su.conversionToBase)} per {inForm.watch('baseUnit')||selectedMedIn.baseUnit}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button type="button" onClick={()=>setSalesUnitsForm(p=>p.filter((_,i)=>i!==idx))}
                              className="text-red-400 hover:text-red-600 text-lg">×</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={()=>setStockInOpen(false)}>Cancel</Button>
            <Button type="submit" loading={saving} className="bg-green-600 hover:bg-green-700 text-white">
              <ArrowDownToLine size={15}/> Record Stock In
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── Stock Out Modal ── */}
      <Modal isOpen={stockOutOpen} onClose={()=>setStockOutOpen(false)} title="Stock Out" size="lg">
        <form onSubmit={outForm.handleSubmit(onStockOut)} className="space-y-4">
          <FormField label="Select Medicine" required>
            <MedicineLookup onSelect={onSelectMedOut} selectedMedicine={selectedMedOut}
              onClear={()=>{ setSelectedMedOut(null); outForm.reset(); }}/>
          </FormField>

          {selectedMedOut && (
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Quantity" required>
                <Input type="number" min="1"
                  {...outForm.register('quantity',{required:'Required'})} placeholder="0"/>
              </FormField>
              <FormField label="Unit">
                <Select {...outForm.register('unit')}>
                  {[
                    { unitKey:selectedMedOut.baseUnit, unitName:selectedMedOut.baseUnit, conversionToBase:1 },
                    ...(selectedMedOut.salesUnits||[])
                  ].map(u=>(
                    <option key={u.unitKey} value={u.unitKey}>
                      {u.unitName}{u.conversionToBase>1?` (= ${u.conversionToBase} ${selectedMedOut.baseUnit})`:'(base)'}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Reason">
                <Select {...outForm.register('reason')}>
                  <option value="damaged">Damaged</option>
                  <option value="expired">Expired</option>
                  <option value="transfer">Transfer</option>
                  <option value="adjustment">Adjustment</option>
                  <option value="other">Other</option>
                </Select>
              </FormField>
              <FormField label="Batch Number">
                <Input {...outForm.register('batchNumber')} placeholder="Batch No."/>
              </FormField>
              <div className="col-span-2">
                <FormField label="Notes">
                  <Input {...outForm.register('notes')} placeholder="Optional notes"/>
                </FormField>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={()=>setStockOutOpen(false)}>Cancel</Button>
            <Button type="submit" loading={saving} variant="danger">
              <ArrowUpFromLine size={15}/> Record Stock Out
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── Detail Modal ── */}
      <DetailModal item={detailItem} onClose={()=>setDetailItem(null)}/>

      {/* ── Edit Inventory Record Modal ── */}
      <Modal isOpen={!!editTarget} onClose={()=>setEditTarget(null)} title="Edit Inventory Record" size="md">
        {editTarget && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-semibold text-gray-800">{editTarget.medicine?.commercialName}</p>
              <p className="text-xs text-gray-400">{editTarget.medicine?.genericName}{editTarget.batchNumber?` · Batch: ${editTarget.batchNumber}`:''}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Current Stock (Quantity)" required>
                <Input type="number" min="0" value={editForm.currentStock}
                  onChange={e=>setEditForm(p=>({...p, currentStock:e.target.value}))}/>
              </FormField>
              <FormField label="Batch Number">
                <Input value={editForm.batchNumber}
                  onChange={e=>setEditForm(p=>({...p, batchNumber:e.target.value}))} placeholder="Batch No."/>
              </FormField>
              <FormField label="Purchase Price / base unit">
                <Input type="number" step="0.01" min="0" value={editForm.purchasePrice}
                  onChange={e=>setEditForm(p=>({...p, purchasePrice:e.target.value}))}/>
              </FormField>
              <FormField label="Selling Price / base unit">
                <Input type="number" step="0.01" min="0" value={editForm.sellingPrice}
                  onChange={e=>setEditForm(p=>({...p, sellingPrice:e.target.value}))}/>
              </FormField>
              <FormField label="Expiry Date">
                <Input type="date" value={editForm.expiryDate}
                  onChange={e=>setEditForm(p=>({...p, expiryDate:e.target.value}))}/>
              </FormField>
            </div>
            <FormField label="Notes">
              <Input value={editForm.notes}
                onChange={e=>setEditForm(p=>({...p, notes:e.target.value}))} placeholder="Notes"/>
            </FormField>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="secondary" onClick={()=>setEditTarget(null)}>Cancel</Button>
              <Button onClick={handleEditSave} loading={editSaving}>Save Changes</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Delete Confirm ── */}
      <ConfirmModal isOpen={!!deleteTarget} onClose={()=>setDeleteTarget(null)}
        onConfirm={handleInventoryDelete} loading={deleting}
        title="Delete Inventory Record"
        message={`Permanently delete inventory record for "${deleteTarget?.medicine?.commercialName}"? This cannot be undone.`}
        confirmLabel="Delete"/>
    </div>
  );
}
